/**
 * DataSet selector component for the interactive map
 */

import {
  Component,
  Show,
  For,
  createSignal,
  onMount,
  onCleanup,
} from 'solid-js';
import type { DataSet } from '../types/interfaces';
import { MAP_Z_INDEX, MAP_CONSTANTS } from '../types/constants';
import { TooltipDelayed } from '../../tooltipDelayed/TooltipDelayed';

/**
 * Additional margin (in px) to account for spacing in the header icons section layout calculation.
 */
const HEADER_ICONS_ADDITIONAL_MARGIN = 4;
/**
 * Calculate the width needed for header icons section
 * Accounts for icon sizes, gaps between icons, and additional margin
 */
const HEADER_ICONS_WIDTH =
  MAP_CONSTANTS.MAX_HEADER_ICONS * MAP_CONSTANTS.HEADER_ICON_SIZE +
  (MAP_CONSTANTS.MAX_HEADER_ICONS - 1) * MAP_CONSTANTS.HEADER_ICON_GAP +
  HEADER_ICONS_ADDITIONAL_MARGIN; // 84px total

interface DataSetSelectorProps {
  dataSets?: DataSet[];
  currentDataSetId?: string;
  onDataSetChange?(dataSetId: string): void;
  disabled?: boolean;
}

const DataSetSelector: Component<DataSetSelectorProps> = (props) => {
  const [dropdownOpen, setDropdownOpen] = createSignal(false);
  let containerRef: HTMLDivElement | undefined;

  const getCurrentDataSetName = (): string => {
    if (!props.dataSets || props.dataSets.length === 0) {
      return 'No data available';
    }
    const currentDataSet = props.dataSets.find(
      (ds) => ds.id === props.currentDataSetId
    );
    return currentDataSet?.name || 'Select Data Set';
  };

  const handleDataSetSelect = (dataSetId: string): void => {
    props.onDataSetChange?.(dataSetId);
    setDropdownOpen(false);
  };

  const toggleDropdown = (event: Event): void => {
    event.stopPropagation();
    event.preventDefault();
    if (!props.disabled) {
      setDropdownOpen(!dropdownOpen());
    }
  };

  // Handle click outside to close dropdown
  const handleClickOutside = (event: MouseEvent): void => {
    if (containerRef && !containerRef.contains(event.target as Node)) {
      setDropdownOpen(false);
    }
  };

  onMount(() => {
    document.addEventListener('click', handleClickOutside);
  });

  onCleanup(() => {
    document.removeEventListener('click', handleClickOutside);
  });

  return (
    <div ref={containerRef} class="dataset-selector relative flex-1 mr-2">
      {/* Dropdown Button */}
      <TooltipDelayed text="Select data set" position="bottom">
        <button
          onClick={toggleDropdown}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onMouseUp={(e) => {
            e.stopPropagation();
          }}
          disabled={props.disabled}
          class="flex items-center space-x-2 text-left hover:bg-black/5 rounded transition-colors w-full h-6 px-2 py-1 min-w-0"
        >
          <span
            class="truncate flex-1 text-[var(--modus-wc-color-base-content,#171C1E)] font-semibold not-italic text-sm leading-4 tracking-normal align-middle"
            style={{
              // eslint-disable-next-line @typescript-eslint/naming-convention -- CSS properties must use kebab-case for SolidJS
              'font-family': 'var(--modus-wc-font-family)',
            }}
          >
            {getCurrentDataSetName()}
          </span>
          <span
            class="tc-icon-chevron-down icon-font"
            aria-hidden="true"
            style={{
              width: '16px',
              height: '16px',
              ['font-size']: '16px',
            }}
          ></span>
        </button>
      </TooltipDelayed>

      {/* Dropdown Menu */}
      <Show when={dropdownOpen()}>
        {/* Invisible backdrop to block events to map */}
        <div
          class="fixed inset-0 pointer-events-auto"
          style={{
            // eslint-disable-next-line @typescript-eslint/naming-convention -- CSS properties must use kebab-case for SolidJS
            'z-index': String(MAP_Z_INDEX.DROPDOWN_BACKDROP),
          }}
          onClick={() => setDropdownOpen(false)}
        />

        <div
          class="absolute bg-white border border-gray-300 shadow-lg max-h-60 overflow-y-auto box-border w-full pointer-events-auto rounded-t-sm rounded-b-md"
          style={{
            top: '100%', // Position below the button
            // eslint-disable-next-line @typescript-eslint/naming-convention -- CSS properties must use kebab-case for SolidJS
            'margin-top': '4px', // Reduced margin to prevent off-screen positioning
            left: '-5px',
            right: `${String(HEADER_ICONS_WIDTH)}px`, // Account for header icons width (calculated from icon sizes and spacing)
            // eslint-disable-next-line @typescript-eslint/naming-convention -- CSS properties must use kebab-case for SolidJS
            'z-index': String(MAP_Z_INDEX.DROPDOWN_MENU),
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onMouseUp={(e) => {
            e.stopPropagation();
          }}
        >
          <Show
            when={props.dataSets && props.dataSets.length > 0}
            fallback={
              <div class="px-3 py-2 text-sm text-gray-500">
                No data sets available
              </div>
            }
          >
            <For each={props.dataSets}>
              {(dataSet) => (
                <button
                  class="block w-full text-left px-3 py-2 hover:bg-gray-100 text-sm box-border pointer-events-auto relative z-[1]"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault(); // Prevent any default behavior
                    handleDataSetSelect(dataSet.id);
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }} // Block mousedown events too
                  onMouseUp={(e) => {
                    e.stopPropagation();
                  }} // Block mouseup events too
                >
                  <div class="font-medium">{dataSet.name}</div>
                  {dataSet.description && (
                    <div class="text-xs text-gray-500 truncate">
                      {dataSet.description}
                    </div>
                  )}
                </button>
              )}
            </For>
          </Show>
        </div>
      </Show>
    </div>
  );
};

export default DataSetSelector;
